import { useInvoices } from "@/components/Invoice";
import { downloadFile } from "@/lib/downloadFile";
import { LineItem } from "@/types";
import {
    ActionIcon,
    Badge,
    Box,
    Group,
    Paper,
    Stack,
    Table,
    Text,
    Tooltip,
} from "@mantine/core";
import { IconDownload, IconReceipt } from "@tabler/icons-react";
import dayjs from "dayjs";

const CompletedLineItems: React.FC = () => {
    const { lineItems: completedLineItems } = useInvoices();
    // const { addToCartByDocument, isInCart, loading: cartLoading } = useCart();

    const formatPrice = (price: number) => {
        return `$${price.toFixed(2)}`;
    };

    const handleDownload = (doc: LineItem) =>
        downloadFile(`/document/download-purchased/${doc.id}`, `${doc.documentNumber}.pdf`);

    const getDaysUntilExpiration = (expiresAt: string): number => {
        return dayjs(expiresAt).diff(dayjs(), "day");
    };

    // const handleAddToCart = async (doc: LineItem) => {
    //     if (!doc.parsedDocumentName || !doc.documentDate) {
    //         notifications.show({
    //             title: "Error",
    //             message: "Document is missing required information",
    //             color: "red",
    //             position: "top-center",
    //             "aria-live": "polite",
    //             closeButtonProps:{
    //                 "aria-label": "Close error notification"
    //             }
    //         });
    //         return;
    //     }

    //     await addToCartByDocument(
    //         doc.documentNumber,
    //         doc.county,
    //         doc.parsedDocumentName,
    //         doc.documentDate
    //     );
    // };

    const getExpirationBadge = (doc: LineItem) => {
        if (doc.isExpired) {
            return (
                <Badge
                    color='red'
                    variant='light'
                >
                    Expired
                </Badge>
            );
        }
        const daysLeft = getDaysUntilExpiration(doc.expiresAt);
        if (daysLeft <= 30) {
            return (
                <Badge
                    color='yellow'
                    variant='light'
                >
                    Expires in {daysLeft} days
                </Badge>
            );
        }
        return (
            <Badge
                color='green'
                variant='light'
            >
                Active
            </Badge>
        );
    };

    // Don't render anything if no purchases
    if (completedLineItems.length === 0) {
        return (
            <Paper
                p='xl'
                withBorder
            >
                <Stack
                    align='center'
                    gap='md'
                >
                    <IconReceipt
                        size={64}
                        color='var(--mantine-color-gray-5)'
                    />
                    <Text
                        c='dimmed'
                        size='lg'
                    >
                        You haven't purchased any documents yet
                    </Text>
                    <Text
                        c='dimmed'
                        size='sm'
                    >
                        Documents you purchase will appear here for 12
                        months.
                    </Text>
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper
            p='md'
            withBorder
        >
            <Box style={{ overflowX: "auto" }}>
                <Table
                    stickyHeader
                    style={{ minWidth: 700 }}
                >
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Document</Table.Th>
                            <Table.Th>County</Table.Th>
                            <Table.Th>Document Date</Table.Th>
                            <Table.Th>Purchase Date</Table.Th>
                            <Table.Th>Expires</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th
                                style={{ textAlign: "right" }}
                            >
                                Price
                            </Table.Th>
                            <Table.Th
                                style={{ textAlign: "right" }}
                            ></Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {completedLineItems.map((doc) => (
                            <Table.Tr
                                key={doc.id}
                                style={{
                                    opacity: doc.isExpired
                                        ? 0.6
                                        : 1,
                                }}
                            >
                                <Table.Td>
                                    <Stack gap={2}>
                                        <Text fw={500}>
                                            {doc.documentNumber}
                                        </Text>
                                    </Stack>
                                </Table.Td>
                                <Table.Td>{doc.county}</Table.Td>
                                <Table.Td>
                                    {doc.documentDate
                                        ? dayjs(
                                              doc.documentDate
                                          ).format("MM/DD/YYYY")
                                        : "-"}
                                </Table.Td>
                                <Table.Td>
                                    {dayjs(doc.purchaseDate).format(
                                        "MM/DD/YYYY"
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    {dayjs(doc.expiresAt).format(
                                        "MM/DD/YYYY"
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    {getExpirationBadge(doc)}
                                </Table.Td>
                                <Table.Td
                                    style={{ textAlign: "right" }}
                                >
                                    {formatPrice(doc.price)}
                                </Table.Td>
                                <Table.Td>
                                    <Group
                                        gap={4}
                                        justify='flex-end'
                                    >
                                        {doc.isExpired || (
                                        //     <Tooltip
                                        //         label={
                                        //             isInCart(doc.documentNumber, doc.county)
                                        //                 ? "Already in cart"
                                        //                 : "Add to cart"
                                        //         }
                                        //     >
                                        //         <ActionIcon
                                        //             onClick={() =>
                                        //                 handleAddToCart(doc)
                                        //             }
                                        //             radius='xl'
                                        //             size='sm'
                                        //             color='pq-red'
                                        //             // variant='light'
                                        //             disabled={
                                        //                 cartLoading ||
                                        //                 isInCart(doc.documentNumber, doc.county)
                                        //             }
                                        //         >
                                        //             <IconShoppingCart
                                        //                 size={15}
                                        //             />
                                        //         </ActionIcon>
                                        //     </Tooltip>
                                        // ) : (
                                            <Tooltip label="Download document">
                                                <ActionIcon
                                                    onClick={() =>
                                                        handleDownload(doc)
                                                    }
                                                    radius='xl'
                                                    size='sm'
                                                    color='pq-red'
                                                    aria-label={`Download ${doc.documentNumber}`}
                                                    disabled={doc.isExpired}
                                                    // variant='light'
                                                >
                                                    <IconDownload
                                                        size={15}
                                                    />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Box>
        </Paper>
    );
};

export default CompletedLineItems;
